import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { IconButton, InputAdornment, FormControlLabel, Switch, Typography, Divider } from "@material-ui/core";
import NumberFormat from "react-number-format";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        //margin: theme.spacing(1),
        minWidth: 120,
    },
    maxWidth: {
        width: "100%", // Garante que o select ocupe a largura total
    },
}));

const UserSchema = Yup.object().shape({
    name: Yup.string()
        .min(3, "Nome deve ter pelo menos 3 caracteres")
        .max(256, "Nome não pode ultrapassar 256 caracteres")
        .required("Obrigatório"),
    email: Yup.string()
        .email("Email inválido")
        .max(128, "Email não pode ultrapassar 128 caracteres")
        .required("Obrigatório"),
    password: Yup.string()
        .min(5, "Senha deve ter pelo menos 6 caracteres")
        .max(256, "Senha não pode ultrapassar 256 caracteres")
        .when("$id", (id, schema) => {
            return !id ? schema.required("Obrigatório") : schema;
        }),
    whatsappId: Yup.string().nullable(), // Adicionado whatsappId para validação
});

const UserModal = ({ open, onClose, userId }) => {
    const classes = useStyles();
    const initialState = {
        name: "",
        email: "",
        password: "",
        profile: "user",
    };
    const { user: loggedInUser } = useContext(AuthContext);
    const [user, setUser] = useState(initialState);
    const [selectedQueueIds, setSelectedQueueIds] = useState([]);
    const [whatsappId, setWhatsappId] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const { loading, whatsApps } = useWhatsApps();

    const defaultWorkSchedule = [
        { weekday: "Segunda-feira", weekdayEn: "monday", startTime: "", endTime: "" },
        { weekday: "Terça-feira", weekdayEn: "tuesday", startTime: "", endTime: "" },
        { weekday: "Quarta-feira", weekdayEn: "wednesday", startTime: "", endTime: "" },
        { weekday: "Quinta-feira", weekdayEn: "thursday", startTime: "", endTime: "" },
        { weekday: "Sexta-feira", weekdayEn: "friday", startTime: "", endTime: "" },
        { weekday: "Sábado", weekdayEn: "saturday", startTime: "", endTime: "" },
        { weekday: "Domingo", weekdayEn: "sunday", startTime: "", endTime: "" },
    ];

    const [isServiceProvider, setIsServiceProvider] = useState(false);
    const [workSchedule, setWorkSchedule] = useState(defaultWorkSchedule);

    useEffect(() => {
        const fetchUser = async () => {
            if (!userId) return;
            try {
                const { data } = await api.get(`/users/${userId}`);
                setUser((prevState) => ({ ...prevState, ...data }));
                const userQueueIds = data.queues?.map((queue) => queue.id);
                setSelectedQueueIds(userQueueIds);
                setWhatsappId(data.whatsappId ? data.whatsappId : "");
                setIsServiceProvider(Boolean(data.isServiceProvider));
                if (Array.isArray(data.workSchedule) && data.workSchedule.length > 0) {
                    setWorkSchedule(data.workSchedule);
                }
            } catch (err) {
                toastError(err);
            }
        };

        fetchUser();
    }, [userId, open]);

    const handleClose = () => {
        onClose();
        setUser(initialState);
        setIsServiceProvider(false);
        setWorkSchedule(defaultWorkSchedule);
    };

    const handleWorkScheduleChange = (index, field, value) => {
        setWorkSchedule((prev) =>
            prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
        );
    };

    const handleSaveUser = async (values) => {
        const userData = {
            ...values,
            whatsappId,
            queueIds: selectedQueueIds,
            isServiceProvider,
            workSchedule: isServiceProvider ? workSchedule : null,
        };
        try {
            if (userId) {
                await api.put(`/users/${userId}`, userData);
                toast.success(i18n.t("userModal.success"));
            } else {
                await api.post("/users", userData);
                toast.success(i18n.t("userModal.success"));
            }
        } catch (error) {
            console.error("Erro ao salvar usuário:", error);
            toastError(error);
        } finally {
            handleClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            scroll="paper"
        >
            <DialogTitle id="form-dialog-title">
                {userId
                    ? `${i18n.t("userModal.title.edit")}`
                    : `${i18n.t("userModal.title.add")}`}
            </DialogTitle>
            <Formik
                initialValues={user}
                enableReinitialize={true}
                validationSchema={UserSchema}
                onSubmit={(values, actions) => {
                    setTimeout(() => {
                        handleSaveUser(values);
                        actions.setSubmitting(false);
                    }, 400);
                }}
            >
                {({ touched, errors, isSubmitting }) => (
                    <Form>
                        <DialogContent dividers>
                            <Field
                                as={TextField}
                                label={i18n.t("userModal.form.name")}
                                autoFocus
                                name="name"
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                            />
                            <Field
                                as={TextField}
                                label={i18n.t("userModal.form.password")}
                                type={showPassword ? "text" : "password"}
                                name="password"
                                error={touched.password && Boolean(errors.password)}
                                helperText={touched.password && errors.password}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Field
                                as={TextField}
                                label={i18n.t("userModal.form.email")}
                                name="email"
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                            />
                            <Can
                                role={loggedInUser.profile}
                                perform="user-modal:editProfile"
                                yes={() => (
                                    <FormControl
                                        variant="outlined"
                                        className={classes.formControl}
                                        margin="dense"
                                        fullWidth
                                    >
                                        <InputLabel id="profile-selection-input-label">
                                            {i18n.t("userModal.form.profile")}
                                        </InputLabel>
                                        <Field
                                            as={Select}
                                            label={i18n.t("userModal.form.profile")}
                                            name="profile"
                                            labelId="profile-selection-label"
                                            id="profile-selection"
                                            required
                                        >
                                            <MenuItem value="admin">Admin</MenuItem>
                                            <MenuItem value="user">User</MenuItem>
                                        </Field>
                                    </FormControl>
                                )}
                            />
                            <Can
                                role={loggedInUser.profile}
                                perform="user-modal:editQueues"
                                yes={() => (
                                    <QueueSelect
                                        selectedQueueIds={selectedQueueIds}
                                        onChange={(values) => setSelectedQueueIds(values)}
                                    />
                                )}
                            />
                            <Can
                                role={loggedInUser.profile}
                                perform="user-modal:editProfile"
                                yes={() => (
                                    <FormControl
                                        variant="outlined"
                                        margin="dense"
                                        className={classes.maxWidth}
                                        fullWidth
                                    >
                                        <InputLabel>
                                            {i18n.t("userModal.form.whatsapp")}
                                        </InputLabel>
                                        <Field
                                            as={Select}
                                            value={whatsappId}
                                            onChange={(e) => setWhatsappId(e.target.value)}
                                            label={i18n.t("userModal.form.whatsapp")}
                                        >
                                            <MenuItem value=""> </MenuItem>
                                            {whatsApps.map((whatsapp) => (
                                                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                                                    {whatsapp.name}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </FormControl>
                                )}
                            />
                            <Can
                                role={loggedInUser.profile}
                                perform="user-modal:editProfile"
                                yes={() => (
                                    <>
                                        <Divider style={{ margin: "12px 0" }} />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={isServiceProvider}
                                                    onChange={(e) =>
                                                        setIsServiceProvider(e.target.checked)
                                                    }
                                                    color="primary"
                                                />
                                            }
                                            label="É profissional de serviço (agendamento)"
                                        />
                                        {isServiceProvider && (
                                            <>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                    display="block"
                                                    style={{ marginTop: 4, marginBottom: 8 }}
                                                >
                                                    Horário de trabalho. Deixe um dia em branco
                                                    para indicar que não atende nele.
                                                </Typography>
                                                {workSchedule.map((item, index) => (
                                                    <div
                                                        key={item.weekdayEn}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 8,
                                                            marginBottom: 4,
                                                        }}
                                                    >
                                                        <TextField
                                                            label="Dia"
                                                            value={item.weekday}
                                                            disabled
                                                            variant="outlined"
                                                            margin="dense"
                                                            style={{ width: "40%" }}
                                                        />
                                                        <NumberFormat
                                                            label="Início"
                                                            value={item.startTime}
                                                            onValueChange={(v) =>
                                                                handleWorkScheduleChange(
                                                                    index,
                                                                    "startTime",
                                                                    v.formattedValue
                                                                )
                                                            }
                                                            customInput={TextField}
                                                            format="##:##"
                                                            variant="outlined"
                                                            margin="dense"
                                                            style={{ width: "30%" }}
                                                        />
                                                        <NumberFormat
                                                            label="Fim"
                                                            value={item.endTime}
                                                            onValueChange={(v) =>
                                                                handleWorkScheduleChange(
                                                                    index,
                                                                    "endTime",
                                                                    v.formattedValue
                                                                )
                                                            }
                                                            customInput={TextField}
                                                            format="##:##"
                                                            variant="outlined"
                                                            margin="dense"
                                                            style={{ width: "30%" }}
                                                        />
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </>
                                )}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={handleClose}
                                color="secondary"
                                disabled={isSubmitting}
                                variant="outlined"
                            >
                                {i18n.t("userModal.buttons.cancel")}
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                disabled={isSubmitting}
                                variant="contained"
                                className={classes.btnWrapper}
                            >
                                {userId
                                    ? `${i18n.t("userModal.buttons.okEdit")}`
                                    : `${i18n.t("userModal.buttons.okAdd")}`}
                                {isSubmitting && (
                                    <CircularProgress
                                        size={24}
                                        className={classes.buttonProgress}
                                    />
                                )}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default UserModal;
